import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import useInterval from '../../hooks/useInterval';
import { Button, notification, Popover, Spin } from 'antd';
import { nullSafeGetOrElse, roundOffToDecimal } from '../../utils/DataAccessUtils';
import Progress from 'antd/es/progress';

const DELAY_TIME = 3000;
const NOTIFICATION_DISPLAY_TIME = 3000;

interface IOWPoll {
	id: string;
	message: string;
	successMessage: string;
	fetchStatusPromises: any[];
	successMessageMandatory?: boolean;
	onRefresh?: () => any;
	onComplete?: (data: any) => void;
	onDisabledChange?: (disabled: boolean) => void;
	successButtonText?: string;
	renderElementWithProps: (props: any) => React.ReactElement;
}

const OWPoll: FC<IOWPoll> = ({
	id,
	fetchStatusPromises,
	successMessage,
	message,
	renderElementWithProps,
	onRefresh,
	onComplete,
	onDisabledChange,
	successButtonText = 'Refresh',
	successMessageMandatory = false,
}): React.ReactElement => {
	const [delay, setDelay] = useState(DELAY_TIME);
	const [userDismissed, setUserDismissed] = useState(false);
	const [showSuccessMessage, setShowSuccessMessage] = useState(successMessageMandatory);
	const [polling, setPolling] = useState(true);
	const [completedPercentage, setCompletedPercentage] = useState(0);

	//Added to start polling once the successMessageMandatory is turned true
	//This case happens when the upload is initiated
	useEffect(() => {
		successMessageMandatory && setDelay(DELAY_TIME);
	}, [successMessageMandatory]);

	const openNotification = useCallback(() => {
		setShowSuccessMessage(true);
		!userDismissed &&
			notification.open({
				key: id,
				message: (
					<div className="flex flex-col">
						<div className="flex flex-row items-center">
							<Spin size="small" />
							<div className="ml-2">{message}</div>
						</div>
						<Progress percent={completedPercentage || 0} />
					</div>
				),
				placement: 'topRight',
				duration: 0,
				onClose: () => setUserDismissed(true),
			});
	}, [completedPercentage, id, message, userDismissed]);

	useEffect(() => {
		successMessageMandatory && openNotification();
	}, [openNotification, successMessageMandatory]);

	const onRefreshClick = useCallback(() => {
		notification.close(id);
		onRefresh();
	}, [id, onRefresh]);

	const onSuccess = useCallback(() => {
		if (!showSuccessMessage) {
			return;
		}

		setShowSuccessMessage(false);

		notification.success({
			key: id,
			message: (
				<div className="flex-start flex flex-col">
					<div>{successMessage}</div>
					{onRefresh ? (
						<div className="ml-[-1rem]">
							<Button type="link" onClick={onRefreshClick}>
								{successButtonText}
							</Button>
						</div>
					) : null}
				</div>
			),
			duration: 0,
			placement: 'topRight',
		});
	}, [id, onRefresh, onRefreshClick, showSuccessMessage, successButtonText, successMessage]);

	const validProcessRunning = (res) => res && res.length > 0;

	const setProgress = (runningProcess) => {
		const completedRecords = nullSafeGetOrElse('completedRecords', runningProcess, 0);
		const totalRecords = nullSafeGetOrElse('totalRecords', runningProcess, 1);
		setCompletedPercentage(roundOffToDecimal((completedRecords * 100) / totalRecords));
	};

	useInterval(() => {
		setPolling(true);
		Promise.all(fetchStatusPromises.map((fn) => fn()))
			.then((res) => {
				const validRunningProcess = res.find((_) => validProcessRunning(_));
				if (validRunningProcess) {
					setProgress(validRunningProcess[0]);
					openNotification();
				} else {
					onComplete && onComplete(res);
					onSuccess();
					setDelay(null);
				}
			})
			.catch((err) => {
				console.log(`Polling error ${err}`);
				setDelay(null);
			})
			.finally(() => setPolling(false));
	}, delay);

	const disabled = useMemo(() => polling || showSuccessMessage, [polling, showSuccessMessage]);

	useEffect(() => {
		if (onDisabledChange) {
			onDisabledChange(disabled);
		}
	}, [disabled, onDisabledChange]);

	const popOverMessage = useMemo(
		() => (showSuccessMessage ? message : polling ? 'Status Check In Progress' : ''),
		[message, polling, showSuccessMessage]
	);

	return (
		<Popover trigger="hover" content={disabled ? popOverMessage : ''}>
			<div>{renderElementWithProps({ disabled })}</div>
		</Popover>
	);
};

export default OWPoll;
